import {
  faBan,
  faCheck,
  faChevronDown,
  faChevronUp,
  faCircleCheck,
  faCircleMinus,
  faSpinner,
  faTriangleExclamation,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { faCircleCheck as faCircleCheckOutline } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAssignmentGetAutoApproveCheckResultDetails } from 'api/assignment/assignment';
import { AssignmentStatusEnum, AutoApproveResult } from 'api/model';
import AiSparkle from 'components/icons/AiSparkle';
import { formatDate, formatTime } from 'utils/date-helpers';
import { useState } from 'react';
import styled from 'styled-components';

interface ApprovalChecksWidgetProps {
  result?: AutoApproveResult | null;
  assignmentID?: number;
  checkResultId?: number | null;
  assignmentStatusID?: AssignmentStatusEnum;
  autoApprovedAt?: Date | string | null;
  /**
   * Whether the assignment has passed its auto-approve eligibility boundary (02:00
   * Swedish after its start day — see AutoApproveGracePeriod.GetEligibleAt). Only
   * meaningful for approvable assignments — `true` = eligible, will be approved by the
   * next nightly run; `false` = still waiting for the boundary. Null when EndTime is unset.
   */
  gracePeriodPassed?: boolean | null;
  /**
   * When that boundary is (same rule as the manual-approval lock), shown while the
   * assignment is still waiting for it. Null when EndTime is unset.
   */
  autoApproveEligibleAt?: Date | null;
  /**
   * Assignment.AutoApprove. `false` means an admin un-approved the assignment (the
   * un-approval reset in CaseLogic.UpdateCase) — the auto-approve loop then skips it
   * for good, whatever the persisted check result says, so it must be approved manually.
   */
  autoApprove?: boolean | null;
}

export interface CheckItem {
  name: string;
  checks: string[];
  deviations: string[];
  hasDeviations: boolean;
}

export type Verdict =
  | 'auto-approved'
  | 'approved'
  | 'not-checked'
  | 'approvable'
  | 'not-approvable'
  | 'opted-out';

export const TONE = {
  good: '#1f7a32',
  bad: '#bb281e',
  neutral: '#8a8a8a',
  auto: '#1772c9',
};

export const VERDICT_CONFIG: Record<
  Verdict,
  { color: string; label: string; icon?: typeof faCheck }
> = {
  'auto-approved': { color: TONE.auto, label: 'Autogodkänd' }, // icon = AiSparkle
  approved: { color: TONE.good, label: 'Godkänd', icon: faCheck },
  'not-checked': {
    color: TONE.neutral,
    label: 'Inte kontrollerad',
    icon: faCircleMinus,
  },
  approvable: {
    color: TONE.good,
    label: 'Autogodkännbar',
    icon: faCircleCheck,
  },
  'not-approvable': {
    color: TONE.bad,
    label: 'Ej autogodkännbar',
    icon: faTriangleExclamation,
  },
  'opted-out': {
    color: TONE.neutral,
    label: 'Avgodkänd',
    icon: faBan,
  },
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const VerdictRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const VerdictGlyph = styled.span<{ tone: string }>`
  display: inline-flex;
  font-size: 15px;
  color: ${({ tone }) => tone};
`;

const VerdictText = styled.span`
  display: inline-flex;
  flex-direction: column;
  line-height: 1.15;
`;

const VerdictLabel = styled.span<{ tone: string }>`
  font-weight: 600;
  color: ${({ tone }) => tone};
`;

const VerdictMeta = styled.span`
  font-size: 0.82em;
  color: #8a8a8a;
  font-weight: 500;
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  margin-left: auto;
  color: ${({ theme }) => theme.colors.background.button};
  transition: transform 0.2s ease;

  &:hover {
    opacity: 0.7;
  }
`;

const ExpandedView = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ReasonMessage = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const DeviationText = styled.div`
  padding-left: 24px;
  font-size: 0.9em;
`;

const LoadingText = styled.span`
  color: #999;
  font-style: italic;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const GraceRow = styled.div<{ tone: string }>`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  padding-top: 6px;
  border-top: 1px solid #eee;
  font-size: 0.82em;
  color: ${({ tone }) => tone};
`;

export function buildCheckItems(r: AutoApproveResult): CheckItem[] {
  return [
    {
      name: 'Uppdragsutförande',
      checks: r.assignmentExecutionChecks,
      deviations: r.assignmentExcecutionDeviations,
      hasDeviations: r.assignmentExcecutionDeviations.length > 0,
    },
    {
      name: 'Utgifter',
      checks: r.expenseChecks,
      deviations: r.expenseDeviations,
      hasDeviations: r.expenseDeviations.length > 0,
    },
    {
      name: 'Övrigt',
      checks: r.generalChecks,
      deviations: r.generalDeviations,
      hasDeviations: r.generalDeviations.length > 0,
    },
  ];
}

export function getVerdict(
  result: AutoApproveResult | null | undefined,
  assignmentStatusID: AssignmentStatusEnum | undefined,
  autoApprovedAt: Date | string | null | undefined,
  autoApprove: boolean | null | undefined
): Verdict {
  if (assignmentStatusID === AssignmentStatusEnum.Approved) {
    return autoApprovedAt ? 'auto-approved' : 'approved';
  }
  // Un-approved by an admin: the loop's candidate filter excludes AutoApprove === false
  // outright, so the frozen snapshot (often still "approvable") says nothing about what
  // will happen next. Never present it as "Autogodkännbar".
  if (autoApprove === false) {
    return 'opted-out';
  }
  if (!result) {
    return 'not-checked';
  }
  return result.autoApprove ? 'approvable' : 'not-approvable';
}

export const OPTED_OUT_TEXT =
  'Avgodkänd av admin, autogodkänns inte. Godkänn manuellt.';

/** Text for an approvable assignment's eligibility boundary (nightly run, 02:00 Swedish). */
export function getGraceText(
  gracePeriodPassed: boolean,
  autoApproveEligibleAt: Date | string | null | undefined
): string {
  if (gracePeriodPassed) return 'Godkänns vid nästa körning';
  if (!autoApproveEligibleAt) return 'Godkänns automatiskt i nattens körning';
  const at = new Date(autoApproveEligibleAt);
  return `Godkänns automatiskt tidigast ${formatDate(at)} kl. ${formatTime(at)}`;
}

/** The short line under the verdict label ("2 avvikelser", "manuellt", …). */
export function getVerdictMeta(
  verdict: Verdict,
  result: AutoApproveResult | null | undefined,
  autoApprovedAt: Date | string | null | undefined
): string | undefined {
  const deviationCount = result
    ? result.assignmentExcecutionDeviations.length +
      result.expenseDeviations.length +
      result.generalDeviations.length
    : 0;
  if (verdict === 'auto-approved' && autoApprovedAt) {
    const approvedAt = new Date(autoApprovedAt);
    return `${formatDate(approvedAt)} kl. ${formatTime(approvedAt)}`;
  }
  if (verdict === 'approved') return 'manuellt';
  if (verdict === 'opted-out') return 'autogodkänns inte';
  if (verdict === 'not-approvable' && deviationCount > 0) {
    return `${deviationCount} avvikelse${deviationCount > 1 ? 'r' : ''}`;
  }
  return undefined;
}

const ApprovalChecksWidget: React.FC<ApprovalChecksWidgetProps> = ({
  result,
  assignmentID,
  checkResultId,
  assignmentStatusID,
  autoApprovedAt,
  gracePeriodPassed,
  autoApproveEligibleAt,
  autoApprove,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const {
    data: detailResult,
    isLoading,
    isError,
  } = useAssignmentGetAutoApproveCheckResultDetails(
    { checkResultId: checkResultId! },
    { query: { enabled: isExpanded && !!checkResultId } }
  );

  const verdict = getVerdict(
    result,
    assignmentStatusID,
    autoApprovedAt,
    autoApprove
  );
  const config = VERDICT_CONFIG[verdict];
  const canExpand = !!result;

  const meta = getVerdictMeta(verdict, result, autoApprovedAt);

  // For approvable assignments the single verdict icon doubles as the auto-approve
  // eligibility indicator — there is never a second icon. Auto-approval runs nightly
  // (02:00 Swedish after the start day): a filled check means that boundary has
  // passed (the next run picks it up); an outline check means it's still waiting for
  // it. The label stays green either way.
  const graceKnown = gracePeriodPassed !== null && gracePeriodPassed !== undefined;
  const showGraceAsGlyph = verdict === 'approvable' && graceKnown;
  const eligibleText = getGraceText(true, autoApproveEligibleAt);
  const waitingText = getGraceText(false, autoApproveEligibleAt);
  const glyphIcon =
    showGraceAsGlyph && !gracePeriodPassed ? faCircleCheckOutline : config.icon;
  const glyphTone =
    showGraceAsGlyph && !gracePeriodPassed ? TONE.neutral : config.color;
  const optedOutText = OPTED_OUT_TEXT;
  const baseTitle = verdict === 'opted-out' ? optedOutText : config.label;
  const glyphTitle = showGraceAsGlyph
    ? gracePeriodPassed
      ? eligibleText
      : waitingText
    : baseTitle;

  const handleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsExpanded(true);
  };

  const renderCollapsedView = () => (
    <VerdictRow>
      <VerdictGlyph tone={glyphTone} title={glyphTitle}>
        {verdict === 'auto-approved' ? (
          <AiSparkle title="Auto-godkänt" />
        ) : (
          glyphIcon && <FontAwesomeIcon icon={glyphIcon} />
        )}
      </VerdictGlyph>
      <VerdictText>
        <VerdictLabel tone={config.color}>{config.label}</VerdictLabel>
        {meta && <VerdictMeta>{meta}</VerdictMeta>}
      </VerdictText>
      {canExpand && (
        <ToggleButton onClick={handleExpand} title="Visa detaljer">
          <FontAwesomeIcon icon={faChevronDown} />
        </ToggleButton>
      )}
    </VerdictRow>
  );

  const renderExpandedView = () => {
    const expandedItems =
      detailResult && !isError
        ? buildCheckItems(detailResult)
        : result
        ? buildCheckItems(result)
        : [];

    return (
      <ExpandedView>
        {isLoading && (
          <LoadingText>
            <FontAwesomeIcon icon={faSpinner} spin />
            Laddar detaljer...
          </LoadingText>
        )}
        {expandedItems.map((item, itemIndex) => (
          <div key={`check-detail-${itemIndex}`}>
            <ReasonMessage>
              <FontAwesomeIcon
                icon={item.hasDeviations ? faXmark : faCheck}
                color={item.hasDeviations ? 'red' : 'green'}
              />
              <strong title={item.checks.join('\n')}>
                {item.name}
                {!item.hasDeviations && ': OK'}
              </strong>
            </ReasonMessage>
            {!isLoading &&
              item.deviations.map((deviation: string, devIndex: number) => (
                <DeviationText
                  key={`deviation-${assignmentID}-${itemIndex}-${devIndex}`}
                >
                  {deviation}
                </DeviationText>
              ))}
          </div>
        ))}
        {verdict === 'approvable' && graceKnown && (
          <GraceRow tone={gracePeriodPassed ? TONE.good : TONE.neutral}>
            <FontAwesomeIcon
              icon={gracePeriodPassed ? faCircleCheck : faCircleCheckOutline}
            />
            {gracePeriodPassed ? eligibleText : waitingText}
          </GraceRow>
        )}
        {verdict === 'opted-out' && (
          <GraceRow tone={TONE.neutral}>
            <FontAwesomeIcon icon={faBan} />
            {optedOutText}
          </GraceRow>
        )}
        <ToggleButton
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setIsExpanded(false);
          }}
          title="Dölj detaljer"
        >
          <FontAwesomeIcon icon={faChevronUp} />
        </ToggleButton>
      </ExpandedView>
    );
  };

  return (
    <Container>
      {isExpanded && canExpand ? renderExpandedView() : renderCollapsedView()}
    </Container>
  );
};

export default ApprovalChecksWidget;
